/**
 * useProductForm Hook
 * 
 * Manages product form state including field retention functionality.
 * Handles form reset based on action button clicked.
 * 
 * Requirements: 8.2, 8.3, 8.4
 */

import { useState, useCallback } from 'react';
import type {
  ProductFormData,
  RetainableField,
  RetainedFields,
  SubmitAction,
} from '../types/product';
import { DEFAULT_FORM_DATA } from '../types/product';

export interface UseProductFormReturn {
  formData: ProductFormData;
  retainedFields: RetainedFields;
  setFormData: React.Dispatch<React.SetStateAction<ProductFormData>>;
  updateField: <K extends keyof ProductFormData>(field: K, value: ProductFormData[K]) => void;
  toggleRetention: (field: RetainableField) => void;
  isRetained: (field: RetainableField) => boolean;
  resetForm: (action: SubmitAction) => void;
  resetFormWithRetention: (retainedFields: RetainedFields, currentData: ProductFormData) => ProductFormData;
  resetFormComplete: () => ProductFormData;
}

/**
 * Reset form while preserving retained field values
 * 
 * @param retainedFields - Set of fields to retain
 * @param currentData - Current form data
 * @returns New form data with retained values preserved
 */
export function resetFormWithRetention(
  retainedFields: RetainedFields,
  currentData: ProductFormData
): ProductFormData {
  const newData = { ...DEFAULT_FORM_DATA };
  
  // Preserve values for retained fields
  for (const field of retainedFields) {
    if (field in currentData) {
      // Deep copy arrays and objects
      const value = currentData[field];
      if (Array.isArray(value)) {
        (newData as Record<string, unknown>)[field] = [...value];
      } else if (typeof value === 'object' && value !== null) {
        (newData as Record<string, unknown>)[field] = { ...(value as object) };
      } else {
        (newData as Record<string, unknown>)[field] = value;
      }
    }
  }
  
  // Images are never retained
  newData.images = [];
  
  return newData;
}

/**
 * Reset form to completely empty/default state
 * 
 * @returns Default form data
 */
export function resetFormComplete(): ProductFormData {
  return { ...DEFAULT_FORM_DATA, images: [] };
}

/**
 * Hook for managing product form state with retention
 */
export function useProductForm(): UseProductFormReturn {
  const [formData, setFormData] = useState<ProductFormData>({ ...DEFAULT_FORM_DATA });
  const [retainedFields, setRetainedFields] = useState<RetainedFields>(new Set());

  const updateField = useCallback(<K extends keyof ProductFormData>(
    field: K,
    value: ProductFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleRetention = useCallback((field: RetainableField) => {
    setRetainedFields(prev => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });
  }, []);

  const isRetained = useCallback((field: RetainableField): boolean => {
    return retainedFields.has(field);
  }, [retainedFields]);

  const resetForm = useCallback((action: SubmitAction) => {
    switch (action) {
      case 'create':
        // Stay on current form, don't reset
        break;
      case 'createAndAddNew':
        // Reset with retention
        setFormData(resetFormWithRetention(retainedFields, formData));
        break;
      case 'createNew':
        // Complete reset
        setFormData(resetFormComplete());
        break;
    }
  }, [retainedFields, formData]);

  return {
    formData,
    retainedFields,
    setFormData,
    updateField,
    toggleRetention,
    isRetained,
    resetForm,
    resetFormWithRetention,
    resetFormComplete,
  };
}

export default useProductForm;
